import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { workspaceAPI, taskAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { formatDuration, getInitials } from '../lib/utils';
import SpaceTasksSlideOver from '../components/SpaceTasksSlideOver';
import SCurveChart from '../components/SCurveChart';
import {
    Users,
    Layers,
    TrendingUp,
    CheckCircle2,
    Loader2,
    AlertCircle,
} from 'lucide-react';

import MemberTasksPopover from '@/components/MemberTasksPopover';
import { TaskDetailFullModal } from '../components/TaskDetailFullModal';



export default function AdminDashboardPage() {
    const { 
        currentWorkspace, 
        spaces, 
        members, 
        fetchWorkspaces,
        selectWorkspace,
    } = useWorkspace();
    
    const [loading, setLoading] = useState(true);
    const [allTasks, setAllTasks] = useState([]);
    const [memberStats, setMemberStats] = useState([]);
    const [pendingTaskStats, setPendingTaskStats] = useState([]);
    const [spaceStats, setSpaceStats] = useState([]);
    
    // Pagination state for Performance Task Member
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);

    // SlideOver state
    const [selectedSpace, setSelectedSpace] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);
    const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);
    const [isMemberSlideOverOpen, setIsMemberSlideOverOpen] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    

    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedTask(null);
    };

    useEffect(() => {
        initPage();
    }, []);

    useEffect(() => {
        if (currentWorkspace) {
            fetchAllTasks();
        }
    }, [currentWorkspace]);

    useEffect(() => {
        if (currentWorkspace && allTasks.length >= 0 && members.length >= 0) {
            calculateStats();
        }
    }, [allTasks, members, spaces]);

    const initPage = async () => {
        if (!currentWorkspace) {
            const ws = await fetchWorkspaces();
            // if (ws.length > 0) {
            //     await selectWorkspace(ws[0]);
            // }
        }
        setLoading(false);
    };

    // Fetch ALL tasks from workspace (not filtered by space or personal)
    const fetchAllTasks = async () => {
        try {
            // Fetch all tasks including subtasks (general/all time)
            const response = await taskAPI.getByWorkspace(currentWorkspace.id, { include_subtasks: true });
            setAllTasks(response.data || []);
        } catch (error) {
            console.error('Failed to fetch all tasks:', error);
            setAllTasks([]);
        }
    };

    // Helper function to map status to status_group
    const mapStatusToGroup = (status) => {
        if (!status) return 'Not Started';
        
        const statusLower = status.toLowerCase();
        
        if (statusLower === 'done') return 'Done';
        if (statusLower === 'in progress' || statusLower === 'blocked') return 'Active';
        if (statusLower === 'review') return 'Review';
        
        return 'Not Started'; // To Do, Backlog, etc.
    };

    const calculateStats = async () => {
        // Filter members - exclude client and guest roles
        const activeMembers = members.filter(m => 
            m.role && !['client', 'guest'].includes(m.role.toLowerCase())
        );

        // Identify parent tasks that have subtasks
        const parentIdsWithSubtasks = new Set(
            allTasks
                .filter(t => t.parent_task_id != null)
                .map(t => t.parent_task_id)
        );

        // Filter tasks for counting:
        // - Subtasks: ALWAYS counted
        // - Parent tasks WITHOUT subtasks: counted (regardless of assignee)
        // - Parent tasks WITH subtasks AND NO assignees: NOT counted (subtasks are counted instead)
        // - Parent tasks WITH subtasks AND HAS assignees: counted
        const countableTasks = allTasks.filter(t => {
            const isSubtask = t.parent_task_id != null;
            const hasAssignees = t.assignee_ids && t.assignee_ids.length > 0;
            const hasSubtasks = parentIdsWithSubtasks.has(t.id);
            
            // Subtasks are always counted
            if (isSubtask) return true;
            
            // Parent task with subtasks but no assignees = exclude (subtasks are counted)
            if (hasSubtasks && !hasAssignees) return false;
            
            // All other tasks are counted (parent with assignees, or task without subtasks)
            return true;
        });

        // Calculate member performance for ALL members (not just those with tasks)
        // Show ALL members in workspace, even if they have 0 tasks
        const memberPerf = activeMembers.map(member => {
            const memberTasks = countableTasks.filter(t => t.assignee_ids?.includes(member.user_id));
            
            // Count by status_group with fallback
            const doneCount = memberTasks.filter(t => {
                const sg = t.status_group || mapStatusToGroup(t.status);
                return sg === 'Done';
            }).length;
            
            const activeCount = memberTasks.filter(t => {
                const sg = t.status_group || mapStatusToGroup(t.status);
                return sg === 'Active';
            }).length;
            
            const reviewCount = memberTasks.filter(t => {
                const sg = t.status_group || mapStatusToGroup(t.status);
                return sg === 'Review';
            }).length;
            
            const total = memberTasks.length;
            
            return {
                ...member,
                totalTasks: total,
                completedTasks: doneCount,
                activeTasks: activeCount,
                reviewTasks: reviewCount,
                completionRate: total > 0 ? (doneCount / total * 100) : 0,
                activeRate: total > 0 ? (activeCount / total * 100) : 0,
                reviewRate: total > 0 ? (reviewCount / total * 100) : 0,
            };
        }).sort((a, b) => b.completedTasks - a.completedTasks); // Sort by completed tasks
        
        setMemberStats(memberPerf);

        // Calculate pending tasks by member (To Do status) - sorted by most pending desc
        const pendingStats = activeMembers.map(member => {
            const memberTasks = countableTasks.filter(t => t.assignee_ids?.includes(member.user_id));
            const toDo = memberTasks.filter(t => t.status_group === "Not Started").length;
            
            return {
                ...member,
                toDoTasks: toDo,
            };
        }).filter(m => m.toDoTasks > 0).sort((a, b) => b.toDoTasks - a.toDoTasks);
        
        setPendingTaskStats(pendingStats);

        // Calculate space stats - using ALL tasks per space (not filtered countableTasks)
        const spacePerf = spaces.map(space => {
            // Use ALL tasks for accurate space count (not filtered)
            const spaceTasks = allTasks.filter(t => t.space_id === space.id);
            const total = spaceTasks.length;
            
            // Count by status_group, with fallback to status field
            const doneCount = spaceTasks.filter(t => {
                const sg = t.status_group || mapStatusToGroup(t.status);
                return sg === 'Done';
            }).length;
            
            const activeCount = spaceTasks.filter(t => {
                const sg = t.status_group || mapStatusToGroup(t.status);
                return sg === 'Active';
            }).length;
            
            const reviewCount = spaceTasks.filter(t => {
                const sg = t.status_group || mapStatusToGroup(t.status);
                return sg === 'Review';
            }).length;
            
            return {
                ...space,
                totalTasks: total,
                completedTasks: doneCount,
                activeTasks: activeCount,
                reviewTasks: reviewCount,
                completionRate: total > 0 ? (doneCount / total * 100) : 0,
                activeRate: total > 0 ? (activeCount / total * 100) : 0,
                reviewRate: total > 0 ? (reviewCount / total * 100) : 0,
            };
        }).filter(s => s.totalTasks > 0); // Only show spaces with tasks
        
        setSpaceStats(spacePerf);
    };

    // Identify parent tasks that have subtasks for summary stats
    const parentIdsWithSubtasksForSummary = new Set(
        allTasks
            .filter(t => t.parent_task_id != null)
            .map(t => t.parent_task_id)
    );

    // Filter tasks for summary stats:
    // - Subtasks: ALWAYS counted
    // - Parent tasks WITHOUT subtasks: counted (regardless of assignee)
    // - Parent tasks WITH subtasks AND NO assignees: NOT counted
    // - Parent tasks WITH subtasks AND HAS assignees: counted
    const countableTasksForSummary = allTasks.filter(t => {
        const isSubtask = t.parent_task_id != null;
        const hasAssignees = t.assignee_ids && t.assignee_ids.length > 0;
        const hasSubtasks = parentIdsWithSubtasksForSummary.has(t.id);
        
        if (isSubtask) return true;
        if (hasSubtasks && !hasAssignees) return false;
        return true;
    });

    // Calculate summary stats from countable tasks
    const totalTasks = countableTasksForSummary.length;
    const completedTasks = countableTasksForSummary.filter(t => t.status === 'Done').length;
    const pendingTasks = countableTasksForSummary.filter(t => t.status === 'To Do').length;
    const overallCompletionRate = totalTasks > 0 ? (completedTasks / totalTasks * 100) : 0;
    
    // Count active members (exclude client & guest)
    const activeMemberCount = members.filter(m => 
        m.role && !['client', 'guest'].includes(m.role.toLowerCase())
    ).length;

    if (loading) {
        return (
            <Layout title="Dashboard Admin">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    if (!currentWorkspace) {
        return (
            <Layout title="Dashboard Admin">
                <div className="empty-state">
                    <p className="text-brand-text-muted">Please select a workspace first</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Dashboard Admin" subtitle="Overview workspace performance (All Time)">
            <div className="space-y-6" data-testid="admin-dashboard">
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary">Total Spaces</p>
                                    <p className="text-2xl font-bold text-brand-text-primary">{spaces.length}</p>
                                </div>
                                <Layers className="w-8 h-8 text-brand-primary opacity-50" strokeWidth={1.5} />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary">Team Members</p>
                                    <p className="text-2xl font-bold text-brand-text-primary">{activeMemberCount}</p>
                                </div>
                                <Users className="w-8 h-8 text-brand-info opacity-50" strokeWidth={1.5} />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary">Task Selesai</p>
                                    <p className="text-2xl font-bold text-brand-success">
                                        {completedTasks} <span className="text-sm font-normal text-brand-text-muted">/ {totalTasks}</span>
                                    </p>
                                </div>
                                <CheckCircle2 className="w-8 h-8 text-brand-success opacity-50" strokeWidth={1.5} />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary">Completion Rate</p>
                                    <p className="text-2xl font-bold text-brand-primary">
                                        {overallCompletionRate.toFixed(0)}%
                                    </p>
                                </div>
                                <TrendingUp className="w-8 h-8 text-brand-primary opacity-50" strokeWidth={1.5} />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* S-Curve Chart */}
                <SCurveChart workspaceId={currentWorkspace.id} spaces={spaces} />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Space Performance */}
                    <Card className="border-brand-border shadow-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Layers className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                                Space yang Berjalan
                            </CardTitle>
                            <CardDescription>Perbandingan task Done vs Total per Space</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {spaceStats.length > 0 ? (
                                <div className="space-y-4">
                                    {spaceStats.map(space => (
                                        <div 
                                            key={space.id} 
                                            className="space-y-2 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
                                            onClick={() => {
                                                setSelectedSpace(space);
                                                setIsSlideOverOpen(true);
                                            }}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div 
                                                        className="w-3 h-3 rounded-full"
                                                        style={{ backgroundColor: space.color }}
                                                    />
                                                    <span className="font-medium text-sm group-hover:text-brand-primary transition-colors">
                                                        {space.name}
                                                    </span>
                                                </div>
                                                <span className="text-sm text-brand-text-secondary">
                                                    <span className="font-semibold text-brand-success">{space.completedTasks}</span>
                                                    <span className="text-brand-text-muted"> / {space.totalTasks}</span>
                                                </span>
                                            </div>
                                            
                                            {/* Multi-segment Progress Bar */}
                                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
                                                {/* Done Segment (Primary Green) */}
                                                {space.completionRate > 0 && (
                                                    <div 
                                                        className="bg-primary hover:opacity-90 transition-all relative group/segment"
                                                        style={{ width: `${space.completionRate}%` }}
                                                    >
                                                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/segment:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                                            {space.completedTasks} Done
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Active Segment (Blue) */}
                                                {space.activeRate > 0 && (
                                                    <div 
                                                        className="bg-blue-500 hover:bg-blue-600 transition-colors relative group/segment"
                                                        style={{ width: `${space.activeRate}%` }}
                                                    >
                                                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/segment:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                                            {space.activeTasks} Active
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Review Segment (Yellow) */}
                                                {space.reviewRate > 0 && (
                                                    <div 
                                                        className="bg-yellow-500 hover:bg-yellow-600 transition-colors relative group/segment"
                                                        style={{ width: `${space.reviewRate}%` }}
                                                    >
                                                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/segment:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                                            {space.reviewTasks} Review
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-brand-text-muted py-4">Belum ada space dengan task</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Member Task Performance - ALL members with pagination */}
                    <Card className="border-brand-border shadow-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5 text-brand-success" strokeWidth={1.5} />
                                Performance Task Member
                            </CardTitle>
                            <CardDescription>
                                Task selesai per member (tanpa Client & Guest) - Showing {Math.min(itemsPerPage, memberStats.length - (currentPage - 1) * itemsPerPage)} of {memberStats.length} members
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {memberStats.length > 0 ? (
                                <div className="space-y-4">
                                    {/* Member List with Pagination */}
                                    {memberStats
                                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                        .map((member, index) => {
                                            const globalIndex = (currentPage - 1) * itemsPerPage + index;
                                            return (
                                                <div 
                                                    key={member.user_id} 
                                                    className="space-y-2 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
                                                    onClick={() => {
                                                        setSelectedMember(member);
                                                        setIsMemberSlideOverOpen(true);
                                                    }}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 text-center flex-shrink-0">
                                                            <span className={`text-sm font-bold ${globalIndex < 3 ? 'text-brand-primary' : 'text-brand-text-muted'}`}>
                                                                #{globalIndex + 1}
                                                            </span>
                                                        </div>
                                                        <div className="avatar w-10 h-10 bg-brand-primary text-white text-sm flex-shrink-0">
                                                            {getInitials(member.user_name)}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <div>
                                                                    <span className="font-medium text-sm truncate block group-hover:text-brand-primary transition-colors">
                                                                        {member.user_name}
                                                                    </span>
                                                                    <span className="text-xs text-brand-text-muted capitalize">{member.role}</span>
                                                                </div>
                                                                <span className="text-sm text-brand-text-secondary">
                                                                    <span className="font-semibold text-brand-success">{member.completedTasks}</span>
                                                                    <span className="text-brand-text-muted"> / {member.totalTasks}</span>
                                                                </span>
                                                            </div>
                                                            
                                                            {/* Progress Bar - Show only if member has tasks */}
                                                            {member.totalTasks > 0 ? (
                                                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
                                                                    {/* Done Segment (Primary Green) */}
                                                                    {member.completionRate > 0 && (
                                                                        <div 
                                                                            className="bg-primary hover:opacity-90 transition-all relative group/segment"
                                                                            style={{ width: `${member.completionRate}%` }}
                                                                        >
                                                                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/segment:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                                                                {member.completedTasks} Done
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* Active Segment (Blue) */}
                                                                    {member.activeRate > 0 && (
                                                                        <div 
                                                                            className="bg-blue-500 hover:bg-blue-600 transition-colors relative group/segment"
                                                                            style={{ width: `${member.activeRate}%` }}
                                                                        >
                                                                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/segment:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                                                                {member.activeTasks} Active
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* Review Segment (Yellow) */}
                                                                    {member.reviewRate > 0 && (
                                                                        <div 
                                                                            className="bg-yellow-500 hover:bg-yellow-600 transition-colors relative group/segment"
                                                                            style={{ width: `${member.reviewRate}%` }}
                                                                        >
                                                                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/segment:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                                                                {member.reviewTasks} Review
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="text-xs text-brand-text-muted italic">No tasks assigned</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    
                                    {/* Pagination Controls */}
                                    {memberStats.length > itemsPerPage && (
                                        <div className="flex items-center justify-between pt-4 border-t border-brand-border">
                                            <div className="text-sm text-brand-text-muted">
                                                Page {currentPage} of {Math.ceil(memberStats.length / itemsPerPage)}
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                    disabled={currentPage === 1}
                                                    className="px-3 py-1 text-sm border border-brand-border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Previous
                                                </button>
                                                <button
                                                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(memberStats.length / itemsPerPage), p + 1))}
                                                    disabled={currentPage >= Math.ceil(memberStats.length / itemsPerPage)}
                                                    className="px-3 py-1 text-sm border border-brand-border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-center text-brand-text-muted py-4">Tidak ada data member</p>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Pending Tasks by Member */}
                <Card className="border-brand-border shadow-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-brand-warning" strokeWidth={1.5} />
                            Pending Task
                        </CardTitle>
                        <CardDescription>Jumlah task dengan status "To Do" per member (urutkan dari terbanyak)</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {pendingTaskStats.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {pendingTaskStats.map((member, index) => (

                                    <MemberTasksPopover 
                                        key={member.user_id} 
                                        userId={member.user_id}
                                        workspaceId={currentWorkspace.id}
                                        handleTaskClick={handleTaskClick}
                                        >
                                        {({ open, setOpen }) => (
                                            <div 
                                                key={member.user_id}
                                                className={`p-4 rounded-lg border ${
                                                    index === 0 ? 'bg-red-50 border-red-200' :
                                                    index < 3 ? 'bg-yellow-50 border-yellow-200' :
                                                    'bg-brand-accent-soft/30 border-brand-border'
                                                }`}>
                                                <div className="flex items-center gap-3">
                                                    <div className="avatar w-10 h-10 bg-brand-primary text-white text-sm">
                                                        {getInitials(member.user_name)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-sm truncate">{member.user_name}</p>
                                                        <p className="text-xs text-brand-text-muted capitalize">{member.role}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className={`text-2xl font-bold ${
                                                            index === 0 ? 'text-red-600' :
                                                            index < 3 ? 'text-yellow-600' :
                                                            'text-brand-warning'
                                                        }`}>
                                                            {member.toDoTasks}
                                                        </p>
                                                        <p className="text-xs text-brand-text-muted">To Do</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </MemberTasksPopover>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <CheckCircle2 className="w-12 h-12 text-brand-success mx-auto mb-3 opacity-50" />
                                <p className="text-brand-text-muted">
                                    Tidak ada task pending! Semua member sudah mengerjakan task mereka.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            
            {/* Space Tasks SlideOver */}
            <SpaceTasksSlideOver
                isOpen={isSlideOverOpen}
                onClose={() => {
                    setIsSlideOverOpen(false);
                    setSelectedSpace(null);
                }}
                space={selectedSpace}
                members={members}
            />

                {/* Task Detail Full Modal */}
                {selectedTask && isModalOpen && (
                    <TaskDetailFullModal
                        isOpen={isModalOpen}
                        onClose={handleModalClose}
                        task={selectedTask}
                        listId={selectedTask?.list_id}
                        statuses={[]}
                        members={members}
                        onTaskUpdate={() => {}}
                    />
                )}


            
            {/* Member Tasks SlideOver */}
            {selectedMember && (
                <SpaceTasksSlideOver
                    isOpen={isMemberSlideOverOpen}
                    onClose={() => {
                        setIsMemberSlideOverOpen(false);
                        setSelectedMember(null);
                    }}
                    space={{
                        id: `member-${selectedMember.user_id}`,
                        name: `Tasks - ${selectedMember.user_name}`,
                        color: '#2F6F63'
                    }}
                    members={members}
                    filterByMember={selectedMember.user_id}
                />
            )}
        </Layout>
    );
}
